import { Divider as MuiDivider } from "@mui/material";
import { spacing } from "@mui/system";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { setSnackbar } from "../../modules/snackbar/api/snackbar";
import StandardHeader from "../../components/global/StandardHeader";
import { LeftSidebar as NavFrame } from "layout-blueprints";
import {
  get as getProperties,
  getByGroup,
  createPropertyGroup,
  updatePropertyGroup,
  deletePropertyGroup,
  put as updateProperty,
  getPropertyGroups
} from "../../modules/property/api/actions";
import { get as getAccountDisconnects } from "../../modules/accountDisconnects/api/actions";
import { get as getAccounts } from "../../modules/accountsSearch/api/actions";
import { get as getMeters } from "../../modules/meter/api/actions";
import { get as getDevelopers } from "../../modules/developer/api/actions";
import { get as getOwners } from "../../modules/owner/api/actions";
import { get as getPropertyManagers } from "../../modules/propertyManager/api/actions";
import PropertyTable from "../../modules/property/components/Table";
import PropertyGroupTable from "../../modules/propertyGroups/components/Table";

import {
  getAllAccountStatusCodes,
  getAllCustomerTypes,
  getAllPropertyPreviews,
  getAllPropertyStatuses,
} from "modules/apl/api/actions";

import {
  AppBar,
  Button,
  Tab,
  Tabs,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";

import {
  Add as AddIcon,
  People as PeopleIcon,
} from "@mui/icons-material";

const Divider = styled(MuiDivider)(spacing);

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tabpanel-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}



const DisconnectManagementPage = (props) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState(0);
  const [newGroupModalOpen, setNewGroupModalOpen] = useState(false);
  const [editGroupModalOpen, setEditGroupModalOpen] = useState(false);
  const [addPropertiesModalOpen, setAddPropertiesModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroupForm, setNewGroupForm] = useState({
    name: '',
    description: '',
    tags: '',
    bgColorTag: '#3341ff',
    isRemote: 0,
    active: 1,
  });
  const [addPropertiesForm, setAddPropertiesForm] = useState({
    selectedGroup: null,
    selectedProperties: [],
    availableProperties: [],
    groupProperties: [],
  });

  useEffect(() => {
    props.getProperties(null, true); // Pass true for isLoading to show loading state
    props.getAccountDisconnects();
    props.getAccounts();
    props.getMeters();
    props.getAllCustomerTypes();
    props.getAllAccountStatusCodes();
    props.getAllPropertyPreviews();
    props.getAllPropertyStatuses();
    props.getPropertyGroups();
    props.getDevelopers();
    props.getOwners();
    props.getPropertyManagers();
    document.title = "Ubil+ Property & Group Management";
  }, []);

  useEffect(() => {
    const groupId = searchParams.get('groupId');
    if (groupId) {
      setValue(0);
      props.getByGroup(groupId);
    } else {
      props.getProperties(null, true); // Pass true for isLoading to show loading state
    }
  }, [searchParams]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleNewGroup = () => {
    setNewGroupModalOpen(true);
  };

  const handleCloseNewGroupModal = () => {
    setNewGroupModalOpen(false);
    setNewGroupForm({
      name: '',
      description: '',
      tags: '',
      bgColorTag: '#FF5733',
      isRemote: 0,
      active: 1,
    });
  };

  const handleFormChange = (field, event) => {
    const {value} = event.target;
    setNewGroupForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setNewGroupForm({
      name: group.name || '',
      description: group.description || '',
      tags: group.tags || '',
      bgColorTag: group.bgColorTag ? `#${group.bgColorTag}` : '#FF5733',
      isRemote: group.isRemote || 0,
      active: group.active || 1,
    });
    setEditGroupModalOpen(true);
  };

  const handleCloseEditGroupModal = () => {
    setEditGroupModalOpen(false);
    setEditingGroup(null);
    setNewGroupForm({
      name: '',
      description: '',
      tags: '',
      bgColorTag: '#FF5733',
      isRemote: 0,
      active: 1,
    });
  };

  const handleSaveNewGroup = async () => {
    try {
      if (editingGroup) {
        const groupUuid =  editingGroup.uuid;
        await props.updatePropertyGroup(groupUuid, newGroupForm);
        handleCloseEditGroupModal();
      } else {
        await props.createPropertyGroup(newGroupForm);
        handleCloseNewGroupModal();
      }
      props.getProperties(null, null);
    } catch (error) {
      console.log("Error saving property group:", error);
      // Handle error - todo show a snackbar
    }
  };

  const handleNewProperty = () => {
    setAddPropertiesModalOpen(true);
  };

  const handleCloseAddPropertiesModal = () => {
    setAddPropertiesModalOpen(false);
    setAddPropertiesForm({
      selectedGroup: null,
      selectedProperties: [],
      availableProperties: [],
      groupProperties: [],
    });
  };

  const handleAddPropertiesFormChange = (field, value) => {
    setAddPropertiesForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGroupSelection = async (group) => {
    if (group) {
      // Refresh data to get latest property information
      await Promise.all([
        props.getProperties(null, null),
        props.getPropertyGroups()
      ]);
      
      const updatedGroup = props.properties?.propertyGroups?.items?.find(g => g.uuid === group.uuid) || group;
      const groupProperties = updatedGroup.properties || updatedGroup.propertyList || updatedGroup.property_list || updatedGroup.items || [];
      
      const allProperties = props.properties?.data?.propertiesList?.items || [];
      const activeProperties = allProperties.filter(property => property.statusId === 1);
      let availableProperties = activeProperties.filter(property => 
        !groupProperties.some(groupProp => groupProp.uuid === property.uuid)
      );
      
      // Filter out remote properties if selected group is a field group
      if (updatedGroup.isRemote === 0) {
        availableProperties = availableProperties.filter(property => 
          !property.isRemote || property.isRemote === 0
        );
      }
      
      setAddPropertiesForm(prev => ({
        ...prev,
        selectedGroup: updatedGroup,
        groupProperties: groupProperties,
        availableProperties: availableProperties,
        selectedProperties: [],
      }));
    } else {
      setAddPropertiesForm(prev => ({
        ...prev,
        selectedGroup: null,
        groupProperties: [],
        availableProperties: [],
        selectedProperties: [],
      }));
    }
  };

  const handleAddPropertiesToGroup = async () => {
    try {
      if (!addPropertiesForm.selectedGroup || addPropertiesForm.selectedProperties.length === 0) {
        return;
      }
      
      // Remote properties cannot be added to field groups
      const isFieldGroup = addPropertiesForm.selectedGroup.isRemote === 0;
      const remoteProperties = addPropertiesForm.selectedProperties.filter(
        property => property.isRemote === 1 || property.isRemote === true
      );
      
      if (isFieldGroup && remoteProperties.length > 0) {
        const remotePropertyNames = remoteProperties.map(p => p.name).join(', ');
        dispatch(setSnackbar(
          true,
          "error",
          `Remote properties cannot be assigned to field groups. Please remove: ${remotePropertyNames}`
        ));
        return;
      }
      
      // filter out remote properties if group is a field group
      const propertiesToAdd = isFieldGroup
        ? addPropertiesForm.selectedProperties.filter(property => 
            !property.isRemote || property.isRemote === 0
          )
        : addPropertiesForm.selectedProperties;
      
      if (propertiesToAdd.length === 0) {
        return;
      }
      
      const groupId = addPropertiesForm.selectedGroup.id || addPropertiesForm.selectedGroup.uuid;
      const updatePromises = propertiesToAdd.map(property => {
        const propertyUpdate = {
          uuid: property.uuid,
          groupId: groupId
        };
        return props.updateProperty(propertyUpdate, false);
      });
      
      await Promise.all(updatePromises);
      
      await Promise.all([
        props.getProperties(null, null),
        props.getPropertyGroups()
      ]);
      
      const updatedSelectedProperties = propertiesToAdd.map(property => ({
        ...property,
        groupName: addPropertiesForm.selectedGroup.name,
        groupColorTag: addPropertiesForm.selectedGroup.bgColorTag
      }));
      
      const updatedGroup = {
        ...addPropertiesForm.selectedGroup,
        properties: [...addPropertiesForm.groupProperties, ...updatedSelectedProperties]
      };
      
      setAddPropertiesForm(prev => ({
        ...prev,
        selectedGroup: updatedGroup,
        groupProperties: updatedGroup.properties,
        availableProperties: prev.availableProperties.filter(prop => 
          !propertiesToAdd.some(selected => selected.uuid === prop.uuid)
        ),
        selectedProperties: [],
      }));
      
      dispatch(setSnackbar(
        true,
        "success",
        `Successfully added ${propertiesToAdd.length} property(ies) to the group.`
      ));
    } catch (error) {
      console.log("Error adding properties to group:", error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error?.message || 
                          error?.message || 
                          "Failed to add properties to group. Please try again.";
      dispatch(setSnackbar(
        true,
        "error",
        errorMessage
      ));
    }
  };

  const handleRemovePropertyFromGroup = async (property) => {
    try {
      const propertyUpdate = {
        uuid: property.uuid,
        groupId: null
      };
      
      await props.updateProperty(propertyUpdate, false);
      
      await Promise.all([
        props.getProperties(null, null),
        props.getPropertyGroups()
      ]);
      
      const updatedProperty = {
        ...property,
        groupName: null,
        groupColorTag: null
      };
      
      setAddPropertiesForm(prev => ({
        ...prev,
        groupProperties: prev.groupProperties.filter(p => p.uuid !== property.uuid),
        availableProperties: [...prev.availableProperties, updatedProperty],
      }));
    } catch (error) {
      console.log("Error removing property from group:", error);
    }
  };

  const handleDeleteGroup = async (group) => {
    try {
      await props.deletePropertyGroup(group.uuid);
      props.getProperties(null, null);
    } catch (error) {
      console.log("Error deleting group:", error);
    }
  };


  return (
    <NavFrame page={"Property & Group Management"}>
      <StandardHeader 
        title="Property & Group Management" 
        appPath="Property & Group Management"
      />
      <Divider className="mb-3 mt-3" />
      
      <Dialog 
        open={newGroupModalOpen} 
        onClose={handleCloseNewGroupModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Property Group</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Group Name"
                value={newGroupForm.name}
                onChange={(e) => handleFormChange('name', e)}
                required
              />
              
              <TextField
                fullWidth
                label="Description"
                value={newGroupForm.description}
                onChange={(e) => handleFormChange('description', e)}
                multiline
                rows={3}
              />
              
              <TextField
                fullWidth
                label="Tags (comma-separated)"
                value={newGroupForm.tags}
                onChange={(e) => handleFormChange('tags', e)}
                placeholder="e.g., test,default,important"
                helperText="Enter tags separated by commas"
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newGroupForm.isRemote}
                  onChange={(e) => handleFormChange('isRemote', e)}
                  label="Type"
                >
                  <MenuItem value={0}>Field</MenuItem>
                  <MenuItem value={1}>Remote</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newGroupForm.active}
                  onChange={(e) => handleFormChange('active', e)}
                  label="Status"
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
              </FormControl>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  label="Color Tag"
                  value={newGroupForm.bgColorTag}
                  onChange={(e) => handleFormChange('bgColorTag', e)}
                  size="small"
                  sx={{ width: 120 }}
                />
                <input
                  type="color"
                  value={newGroupForm.bgColorTag}
                  onChange={(e) => handleFormChange('bgColorTag', e)}
                  style={{ width: 50, height: 40, border: 'none', borderRadius: 4 }}
                />
                <Chip
                  label="Preview"
                  style={{
                    backgroundColor: newGroupForm.bgColorTag,
                    color: '#ffffff'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewGroupModal}>Cancel</Button>
          <Button 
            onClick={handleSaveNewGroup} 
            variant="contained"
            disabled={!newGroupForm.name.trim() || props.properties?.propertyGroups?.isCreating}
          >
            {props.properties?.propertyGroups?.isCreating ? 'Creating...' : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Modal */}
      <Dialog 
        open={editGroupModalOpen} 
        onClose={handleCloseEditGroupModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Property Group</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Group Name"
                value={newGroupForm.name}
                onChange={(e) => handleFormChange('name', e)}
                required
              />
              
              <TextField
                fullWidth
                label="Description"
                value={newGroupForm.description}
                onChange={(e) => handleFormChange('description', e)}
                multiline
                rows={3}
              />
              
              <TextField
                fullWidth
                label="Tags (comma-separated)"
                value={newGroupForm.tags}
                onChange={(e) => handleFormChange('tags', e)}
                placeholder="e.g., test,default,important"
                helperText="Enter tags separated by commas"
              />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={newGroupForm.isRemote}
                    onChange={(e) => handleFormChange('isRemote', e)}
                    label="Type"
                  >
                    <MenuItem value={0}>Field</MenuItem>
                    <MenuItem value={1}>Remote</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newGroupForm.active}
                    onChange={(e) => handleFormChange('active', e)}
                    label="Status"
                  >
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={0}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  label="Color Tag"
                  value={newGroupForm.bgColorTag}
                  onChange={(e) => handleFormChange('bgColorTag', e)}
                  size="small"
                  sx={{ width: 120 }}
                />
                <input
                  type="color"
                  value={newGroupForm.bgColorTag}
                  onChange={(e) => handleFormChange('bgColorTag', e)}
                  style={{ width: 50, height: 40, border: 'none', borderRadius: 4 }}
                />
                <Chip
                  label="Preview"
                  style={{
                    backgroundColor: newGroupForm.bgColorTag,
                    color: '#ffffff'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditGroupModal}>Cancel</Button>
          <Button 
            onClick={handleSaveNewGroup} 
            variant="contained"
            disabled={!newGroupForm.name.trim() || props.properties?.propertyGroups?.isUpdating}
          >
            {props.properties?.propertyGroups?.isUpdating ? 'Updating...' : 'Update Group'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addPropertiesModalOpen} 
        onClose={handleCloseAddPropertiesModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Manage Group Properties</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Select Group to Manage</InputLabel>
                <Select
                  value={addPropertiesForm.selectedGroup?.uuid || ''}
                  onChange={(e) => {
                    const selectedGroup = props.properties?.propertyGroups?.items?.find(
                      group => group.uuid === e.target.value
                    );
                    handleGroupSelection(selectedGroup);
                  }}
                  label="Select Group to Manage"
                >
                  {props.properties?.propertyGroups?.items?.map((group) => (
                    <MenuItem key={group.uuid} value={group.uuid}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={group.name}
                          size="small"
                          style={{
                            backgroundColor: group.bgColorTag ? `#${group.bgColorTag}` : 'transparent',
                            color: group.bgColorTag ? '#ffffff' : '#000000',
                            border: group.bgColorTag ? 'none' : '1px solid #ccc'
                          }}
                          variant={group.bgColorTag ? "filled" : "outlined"}
                        />
                        <Typography variant="body2" color="textSecondary">
                          ({(group.properties || group.propertyList || group.property_list || group.items || []).length} properties)
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {addPropertiesForm.selectedGroup && (
              <Box sx={{ display: 'flex', gap: 3, height: '500px' }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom>
                    Properties in Group ({addPropertiesForm.groupProperties.length})
                  </Typography>
                  <Box sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    height: '100%', 
                    overflow: 'auto',
                    p: 1
                  }}>
                    {addPropertiesForm.groupProperties.length > 0 ? (
                      addPropertiesForm.groupProperties.map((property) => (
                        <Box 
                          key={property.uuid} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            p: 1,
                            mb: 1,
                            border: '1px solid #f0f0f0',
                            borderRadius: 1,
                            backgroundColor: '#fafafa'
                          }}
                        >
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5, width: '100%' }}>
                              <Typography variant="body2" fontWeight="medium">
                                {property.name}
                              </Typography>
                              {property.groupName && (
                                <Chip
                                  label={property.groupName}
                                  size="small"
                                  style={{
                                    backgroundColor: property.groupColorTag ? `#${property.groupColorTag}` : 'transparent',
                                    color: property.groupColorTag ? '#ffffff' : '#000000',
                                    border: property.groupColorTag ? 'none' : '1px solid #ccc',
                                    fontWeight: '500'
                                  }}
                                  variant={property.groupColorTag ? "filled" : "outlined"}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                              {property.city}, {property.state} {property.zip}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleRemovePropertyFromGroup(property)}
                            sx={{ minWidth: 'auto', px: 1 }}
                          >
                            Remove
                          </Button>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                        No properties in this group
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom>
                    Available Properties ({addPropertiesForm.availableProperties.length})
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Properties to Add</InputLabel>
                    <Select
                      multiple
                      value={addPropertiesForm.selectedProperties.map(p => p.uuid)}
                      onChange={(e) => {
                        const selectedPropertyUuids = e.target.value;
                        const selectedProperties = addPropertiesForm.availableProperties.filter(
                          property => selectedPropertyUuids.includes(property.uuid)
                        );
                        handleAddPropertiesFormChange('selectedProperties', selectedProperties);
                      }}
                      input={<OutlinedInput label="Select Properties to Add" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const property = addPropertiesForm.availableProperties.find(p => p.uuid === value);
                            return property ? (
                              <Chip key={value} label={property.name} size="small" />
                            ) : null;
                          })}
                        </Box>
                      )}
                    >
                      {addPropertiesForm.availableProperties.map((property) => (
                        <MenuItem key={property.uuid} value={property.uuid}>
                          <Checkbox 
                            checked={addPropertiesForm.selectedProperties.some(p => p.uuid === property.uuid)}
                          />
                          <ListItemText 
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{property.name}</span>
                                {property.groupName && (
                                  <Chip
                                    label={property.groupName}
                                    size="small"
                                    style={{
                                      backgroundColor: property.groupColorTag ? `#${property.groupColorTag}` : 'transparent',
                                      color: property.groupColorTag ? '#ffffff' : '#000000',
                                      border: property.groupColorTag ? 'none' : '1px solid #ccc',
                                      fontWeight: '500'
                                    }}
                                    variant={property.groupColorTag ? "filled" : "outlined"}
                                  />
                                )}
                              </Box>
                            }
                            secondary={`${property.city}, ${property.state} ${property.zip}`}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {addPropertiesForm.selectedProperties.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Selected to Add ({addPropertiesForm.selectedProperties.length}):
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {addPropertiesForm.selectedProperties.map((property) => (
                          <Box key={property.uuid} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={property.name}
                              onDelete={() => {
                                const updatedProperties = addPropertiesForm.selectedProperties.filter(
                                  p => p.uuid !== property.uuid
                                );
                                handleAddPropertiesFormChange('selectedProperties', updatedProperties);
                              }}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    onClick={handleAddPropertiesToGroup}
                    disabled={addPropertiesForm.selectedProperties.length === 0}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Add Selected Properties
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddPropertiesModal}>Close</Button>
        </DialogActions>
      </Dialog>
      
      <Box className="mb-3" display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PeopleIcon />}
              onClick={handleNewGroup}
            >
              New Group
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={handleNewProperty}
            >
              Manage Groups
            </Button>
      </Box>

        <AppBar position="static" color="primary">
          <Tabs
            value={value}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            aria-label="Property & Group Management Tabs"
          >
            <Tab label="Properties" {...a11yProps(0)} />
            <Tab label="Property Groups" {...a11yProps(1)} />
          </Tabs>
        </AppBar>

      <TabPanel value={value} index={0}>
            <PropertyTable />
      </TabPanel>

      <TabPanel value={value} index={1}>
                    <PropertyGroupTable onEditGroup={handleEditGroup} onDeleteGroup={handleDeleteGroup} />
      </TabPanel>
    </NavFrame>
  );
};

DisconnectManagementPage.propTypes = {
  user: PropTypes.object.isRequired,
  apl: PropTypes.object.isRequired,
  properties: PropTypes.object.isRequired,
  getAccountDisconnects: PropTypes.func.isRequired,
  getAccounts: PropTypes.func.isRequired,
  getMeters: PropTypes.func.isRequired,
  getProperties: PropTypes.func.isRequired,
  createPropertyGroup: PropTypes.func.isRequired,
  updatePropertyGroup: PropTypes.func.isRequired,
  deletePropertyGroup: PropTypes.func.isRequired,
  updateProperty: PropTypes.func.isRequired,
  getAllPropertyPreviews: PropTypes.func.isRequired,
  getAllAccountStatusCodes: PropTypes.func.isRequired,
  getAllCustomerTypes: PropTypes.func.isRequired,
  getAllPropertyStatuses: PropTypes.func.isRequired,
  getPropertyGroups: PropTypes.func.isRequired,
  getDevelopers: PropTypes.func.isRequired,
  getOwners: PropTypes.func.isRequired,
  getPropertyManagers: PropTypes.func.isRequired,
};

function DisconnectManagementPageState(state) {
  return {
    user: state.user,
    apl: state.apl,
    properties: state.properties,
    accountDisconnects: state.accountDisconnects,
  };
}

export default connect(DisconnectManagementPageState, {
  getAccountDisconnects,
  getAccounts,
  getMeters,
  getProperties,
  getByGroup,
  createPropertyGroup,
  updatePropertyGroup,
  deletePropertyGroup,
  updateProperty,
  getAllPropertyPreviews,
  getAllAccountStatusCodes, 
  getAllCustomerTypes,
  getAllPropertyStatuses,
  getPropertyGroups,
  getDevelopers,
  getOwners,
  getPropertyManagers
})(DisconnectManagementPage);
